import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Tab,
    Tabs,
    TextField,
    Button,
    Stack,
    Divider,
    List,
    ListItem,
    ListItemText,
    IconButton,
    CircularProgress,
    InputAdornment,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Chip,
    OutlinedInput
} from '@mui/material';
import { Delete, Map as MapIcon, Payments, Search, LocationOn } from '@mui/icons-material';
import { useConfiguration, useUpdateConfiguration, useManageStates, useManageCities } from 'hooks/useConfiguration';

// --- Leaflet Imports ---
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix for Leaflet default icon issues in React
const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

// --- Map Sub-Components ---

import PropTypes from 'prop-types';

/**
 * Automatically moves the map view when coordinates change
 */
function ChangeView({ center }) {
    const map = useMap();
    useEffect(() => {
        map.setView(center, 12);
    }, [center, map]);
    return null;
}

ChangeView.propTypes = {
    center: PropTypes.arrayOf(PropTypes.number).isRequired
};

/**
 * Handles clicking on the map to set city coordinates
 */
function MapEvents({ setNewCity }) {
    useMapEvents({
        click(e) {
            const { lat, lng } = e.latlng;
            setNewCity((prev) => ({
                ...prev,
                lng: lng.toFixed(6),
                lat: lat.toFixed(6)
            }));
        }
    });
    return null;
}

MapEvents.propTypes = {
    setNewCity: PropTypes.func.isRequired
};

// --- Main Settings Component ---

export default function Settings() {
    const [activeTab, setActiveTab] = useState(0);

    // 1. Data Hooks
    const { data: config, isLoading } = useConfiguration();
    const updateConfig = useUpdateConfiguration();
    const { addState, deleteState } = useManageStates();
    const { addCity, deleteCity } = useManageCities();

    // 2. UI Feedback States
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

    // 3. Local Form States
    const [formFields, setFormFields] = useState(null);
    const [newState, setNewState] = useState('');
    const [stateSearch, setStateSearch] = useState('');
    const [newCity, setNewCity] = useState({
        name: '',
        state: '',
        lng: '3.35148',
        lat: '6.59673'
    });

    useEffect(() => {
        if (config) setFormFields(config);
    }, [config]);

    const showAlert = (message, severity = 'success') => setAlert({ open: true, message, severity });

    // --- Action Handlers ---

    const handleConfirmDelete = () => {
        if (!deleteTarget) return;
        const { id, name, type } = deleteTarget;
        const mutation = type === 'state' ? deleteState : deleteCity;

        mutation.mutate(id, {
            onSuccess: () => {
                showAlert(`${name} removed successfully`);
                setConfirmOpen(false);
                setDeleteTarget(null);
            },
            onError: (err) => showAlert(err.message || 'Operation failed', 'error')
        });
    };

    const handleRegisterCity = () => {
        if (!newCity.name || !newCity.state) return;

        // Payload formatted for backend (GeoJSON style: [lng, lat])
        const payload = {
            name: newCity.name,
            state: newCity.state,
            coordinates: [parseFloat(newCity.lng), parseFloat(newCity.lat)]
        };

        addCity.mutate(payload, {
            onSuccess: () => {
                showAlert(`${newCity.name} registered`);
                setNewCity({ ...newCity, name: '' }); // Reset name but keep state/coords for batch adding
            },
            onError: (err) => showAlert(err.message || 'Registration failed', 'error')
        });
    };

    // --- Helper Calculations ---

    const getCitiesForState = (stateName) => {
        return config?.serviceCities?.filter((city) => city.state.toLowerCase() === stateName.toLowerCase()) || [];
    };

    if (isLoading || !formFields) {
        return (
            <Box p={10} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress size={60} />
            </Box>
        );
    }

    const filteredStates = config.serviceStates.filter((s) => s.name.toLowerCase().includes(stateSearch.toLowerCase()));

    const mapCenter = [Number(newCity.lat), Number(newCity.lng)];

    return (
        <Box p={{ xs: 2, md: 4 }}>
            {/* Notifications */}
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
                <Alert severity={alert.severity} variant="filled" sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>

            {/* Global Delete Confirmation */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Removal</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleConfirmDelete}>
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Typography variant="h4" fontWeight={800} mb={4} color="primary.main">
                Platform Control Center
            </Typography>

            <Grid container spacing={4}>
                {/* Sidebar Navigation */}
                <Grid item xs={12} md={3}>
                    <Paper sx={{ borderRadius: 3, overflow: 'hidden', elevation: 2 }}>
                        <Tabs
                            orientation="vertical"
                            value={activeTab}
                            onChange={(_, v) => setActiveTab(v)}
                            sx={{ borderRight: 1, borderColor: 'divider', minHeight: 200 }}
                        >
                            <Tab
                                icon={<Payments sx={{ mr: 1 }} />}
                                iconPosition="start"
                                label="Fees & Commissions"
                                sx={{ justifyContent: 'flex-start', py: 2 }}
                            />
                            <Tab
                                icon={<MapIcon sx={{ mr: 1 }} />}
                                iconPosition="start"
                                label="Service Areas"
                                sx={{ justifyContent: 'flex-start', py: 2 }}
                            />
                        </Tabs>
                    </Paper>
                </Grid>

                {/* Main Content Area */}
                <Grid item xs={12} md={9}>
                    {activeTab === 0 && (
                        <Paper sx={{ p: 4, borderRadius: 3 }}>
                            <Typography variant="h6" mb={3} fontWeight={700}>
                                Global Financial Configuration
                            </Typography>
                            <Grid container spacing={3}>
                                {/* Delivery Pricing & Service Fee */}
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Base Delivery Fee"
                                        type="number"
                                        value={formFields.baseDeliveryFee}
                                        onChange={(e) => setFormFields({ ...formFields, baseDeliveryFee: e.target.value })}
                                        InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Fee Per KM"
                                        type="number"
                                        value={formFields.feePerKm}
                                        onChange={(e) => setFormFields({ ...formFields, feePerKm: e.target.value })}
                                        InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Base Service Fee"
                                        type="number"
                                        value={formFields.baseServiceFee}
                                        onChange={(e) => setFormFields({ ...formFields, baseServiceFee: e.target.value })}
                                        InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                                        helperText="Applicable to vendor orders only"
                                    />
                                </Grid>

                                {/* Commissions */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Rider Commission"
                                        type="number"
                                        value={formFields.riderCommission}
                                        onChange={(e) => setFormFields({ ...formFields, riderCommission: e.target.value })}
                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                        helperText="Amount paid to the rider"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Package Commission"
                                        type="number"
                                        value={formFields.packageCommission}
                                        onChange={(e) => setFormFields({ ...formFields, packageCommission: e.target.value })}
                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                        helperText="Platform service fee"
                                    />
                                </Grid>

                                {/* Logistics & Discounts */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Nearby Search Distance"
                                        type="number"
                                        value={formFields.nearbyDistance}
                                        onChange={(e) => setFormFields({ ...formFields, nearbyDistance: e.target.value })}
                                        InputProps={{ endAdornment: <InputAdornment position="end">meters</InputAdornment> }}
                                        helperText="Radius for matching riders (7000 = 7km)"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Pay on Delivery Discount"
                                        type="number"
                                        value={formFields.payOnDeliveryDiscount}
                                        onChange={(e) => setFormFields({ ...formFields, payOnDeliveryDiscount: e.target.value })}
                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                    />
                                </Grid>
                            </Grid>

                            <Button
                                variant="contained"
                                sx={{ mt: 4, px: 4, py: 1.5, borderRadius: 2 }}
                                size="large"
                                disabled={updateConfig.isPending}
                                onClick={() =>
                                    updateConfig.mutate(formFields, {
                                        onSuccess: () => showAlert('Global settings updated successfully!')
                                    })
                                }
                            >
                                {updateConfig.isPending ? <CircularProgress size={24} color="inherit" /> : 'Save Global Changes'}
                            </Button>
                        </Paper>
                    )}{' '}
                    {activeTab === 1 && (
                        <Stack spacing={4}>
                            {/* LISTING SECTION */}
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                                    <Typography variant="h6" fontWeight={700}>
                                        Active Coverage
                                    </Typography>
                                    <OutlinedInput
                                        size="small"
                                        placeholder="Search states..."
                                        value={stateSearch}
                                        onChange={(e) => setStateSearch(e.target.value)}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <Search fontSize="small" />
                                            </InputAdornment>
                                        }
                                        sx={{ borderRadius: 2, width: 250 }}
                                    />
                                </Stack>

                                <List sx={{ width: '100%', border: '1px solid #edf2f7', borderRadius: 2, overflow: 'hidden' }}>
                                    {filteredStates.map((state) => {
                                        const stateCities = getCitiesForState(state.name);
                                        return (
                                            <React.Fragment key={state._id}>
                                                <ListItem
                                                    sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}
                                                    secondaryAction={
                                                        <IconButton
                                                            edge="end"
                                                            color="error"
                                                            onClick={() => {
                                                                setDeleteTarget({ id: state._id, name: state.name, type: 'state' });
                                                                setConfirmOpen(true);
                                                            }}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    }
                                                >
                                                    <ListItemText
                                                        primary={<Typography fontWeight="700">{state.name}</Typography>}
                                                        secondary={`${stateCities.length} active cities`}
                                                    />
                                                </ListItem>
                                                <Box sx={{ p: 2, pl: 4 }}>
                                                    {stateCities.length > 0 ? (
                                                        <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
                                                            {stateCities.map((city) => (
                                                                <Chip
                                                                    key={city._id}
                                                                    label={city.name}
                                                                    onDelete={() => {
                                                                        setDeleteTarget({ id: city._id, name: city.name, type: 'city' });
                                                                        setConfirmOpen(true);
                                                                    }}
                                                                    sx={{ borderRadius: 1.5 }}
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                            ))}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">
                                                            No cities registered in this state
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Divider />
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            </Paper>

                            {/* ADD NEW LOCATION SECTION */}
                            <Paper sx={{ p: 4, borderRadius: 3 }}>
                                <Typography variant="h6" mb={4} fontWeight={700}>
                                    Add New Territory
                                </Typography>
                                <Grid container spacing={5}>
                                    {/* Register State */}
                                    <Grid item xs={12} md={5}>
                                        <Typography variant="subtitle2" mb={2} color="text.secondary" display="flex" alignItems="center">
                                            <LocationOn sx={{ fontSize: 18, mr: 0.5 }} /> Register New State
                                        </Typography>
                                        <Stack spacing={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="State Name"
                                                placeholder="e.g. Lagos"
                                                value={newState}
                                                onChange={(e) => setNewState(e.target.value)}
                                            />
                                            <Button
                                                variant="contained"
                                                disabled={!newState || addState.isPending}
                                                onClick={() =>
                                                    addState.mutate(
                                                        { name: newState },
                                                        {
                                                            onSuccess: () => {
                                                                showAlert(`${newState} added`);
                                                                setNewState('');
                                                            }
                                                        }
                                                    )
                                                }
                                            >
                                                {addState.isPending ? <CircularProgress size={20} /> : 'Add State'}
                                            </Button>
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={12} md={1} display="flex" justifyContent="center">
                                        <Divider orientation="vertical" flexItem />
                                    </Grid>

                                    {/* Register City with Interactive Map */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" mb={2} color="text.secondary">
                                            Register City (Click Map to Pinpoint)
                                        </Typography>
                                        <Stack spacing={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="City Name"
                                                value={newCity.name}
                                                onChange={(e) => setNewCity({ ...newCity, name: e.target.value })}
                                            />
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="Assign to State"
                                                SelectProps={{ native: true }}
                                                value={newCity.state}
                                                onChange={(e) => setNewCity({ ...newCity, state: e.target.value })}
                                            >
                                                <option value="">Select a state</option>
                                                {config.serviceStates.map((s) => (
                                                    <option key={s._id} value={s.name}>
                                                        {s.name}
                                                    </option>
                                                ))}
                                            </TextField>

                                            <Box
                                                sx={{
                                                    height: 250,
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    position: 'relative',
                                                    border: '1px solid #ddd'
                                                }}
                                            >
                                                <MapContainer center={mapCenter} zoom={12} style={{ height: '100%', width: '100%' }}>
                                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                    <Marker position={mapCenter} icon={DefaultIcon} />
                                                    <ChangeView center={mapCenter} />
                                                    <MapEvents setNewCity={setNewCity} />
                                                </MapContainer>
                                                <Box sx={{ position: 'absolute', bottom: 10, left: 10, zIndex: 1000 }}>
                                                    <Chip
                                                        label={`Coords: ${newCity.lat}, ${newCity.lng}`}
                                                        size="small"
                                                        sx={{ bgcolor: 'rgba(255,255,255,0.9)', fontWeight: 'bold' }}
                                                    />
                                                </Box>
                                            </Box>

                                            <Button
                                                variant="contained"
                                                fullWidth
                                                color="secondary"
                                                disabled={!newCity.name || !newCity.state || addCity.isPending}
                                                onClick={handleRegisterCity}
                                            >
                                                {addCity.isPending ? <CircularProgress size={24} color="inherit" /> : 'Register City'}
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Stack>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}
